import { Button, HStack, Text, VStack } from '@chakra-ui/react'
import { NodeProps, Position } from '@xyflow/react'
import { useAtomValue } from 'jotai'
import React, { useMemo } from 'react'

import { ExtensionInstance } from '@mangovoice/types'

import { extensionsListAtom } from '../../atoms'
import { TransferAndRolloverNodeData } from '../../converters/shared/reactFlowTypes'
import { useSwitchboardT } from '../../types'
import { text } from '../../utils/text'
import { ExtensionSelector, NodeInput } from '../core'
import { BaseNode, nodePropsAreEqual } from './BaseNode'
import { useTransferAndRolloverNode } from './hooks/useTransferAndRolloverNode'

export interface TransferAndRolloverNodeProps extends NodeProps {
  extensionMap?: Map<number, ExtensionInstance>
  onDelete?: () => void
  onDeleteNode?: (nodeId: string) => void
  showDeleteButton?: boolean
  // Note: extensionsList is provided via Jotai atoms (extensionsListAtom)
  // Use useSwitchboardDataAtoms hook in the consuming app to set atoms
}

/**
 * Transfer and Rollover node component for call flow diagrams.
 * Allows transferring a call to multiple extensions with a timeout, rolling over to the next extension if no answer.
 */
const TransferAndRolloverNodeComponent: React.FC<
  TransferAndRolloverNodeProps
> = ({
  id,
  data,
  extensionMap: _extensionMap, // Reserved for future use (e.g., display formatting)
  onDelete,
  onDeleteNode,
  showDeleteButton,
}) => {
  // Read from Jotai atom
  const extensionsList = useAtomValue(extensionsListAtom)

  const nodeData = data as TransferAndRolloverNodeData & Record<string, unknown>
  const {
    currentExtensions,
    timeout,
    handleExtensionChange,
    handleRemoveExtension,
    handleTimeoutChange,
    handleTimeoutBlur,
    hasData,
  } = useTransferAndRolloverNode(id, nodeData)

  // Get extension display names for the selected extensions
  const getExtensionDisplay = React.useCallback(
    (extensionId: string): string => {
      if (!extensionsList) return extensionId
      const ext = extensionsList.find((e) => String(e.id) === extensionId)
      return ext ? `${ext.number} - ${ext.name || 'Unnamed'}` : extensionId
    },
    [extensionsList],
  )

  // Show the first extension in the selector (like Transfer & Continue)
  const primaryExtensionId =
    currentExtensions.length > 0 ? currentExtensions[0] : ''
  // Additional extensions beyond the first one
  const additionalExtensions = currentExtensions.slice(1)
  const t = useSwitchboardT()

  // Add two custom handles on the right side: No Answer (top), Busy (bottom)
  // Memoize handles array to prevent re-creating on every render
  const transferAndRolloverHandles = useMemo(
    () => [
      {
        id: 'noAnswer',
        position: Position.Right as Position,
        style: {
          top: '0px', // Handle top at 0px, center at 7px, connection point at ~0%
        } as React.CSSProperties,
        label: text(
          t,
          'switchboard.nodes.transferAndRollover.noAnswer',
          'No Answer',
        ),
      },
      {
        id: 'busy',
        position: Position.Right as Position,
        style: {
          top: 'calc(100%)', // Handle top at 100%, center at 100% - 7px, connection point at ~100%
        } as React.CSSProperties,
        label: text(t, 'switchboard.nodes.transferAndRollover.busy', 'Busy'),
      },
    ],
    [t],
  )

  return (
    <BaseNode
      label={text(
        t,
        'switchboard.nodeLabels.transfer_and_rollover',
        'Transfer & Rollover',
      )}
      showPlaceholder={!hasData}
      sourceHandlePosition={null}
      additionalSourceHandles={transferAndRolloverHandles}
      onDelete={onDelete}
      onDeleteNode={onDeleteNode}
      id={id}
      showDeleteButton={showDeleteButton}
    >
      <VStack align="stretch" gap={2}>
        {/* Extension selector - shows the primary/first extension like Transfer & Continue */}
        <ExtensionSelector
          value={primaryExtensionId}
          onChange={handleExtensionChange}
          filterMode="transfer_rollover"
        />

        {/* Display additional extensions (beyond the first one) */}
        {additionalExtensions.length > 0 && (
          <VStack align="stretch" gap={1}>
            {additionalExtensions.map((extId) => (
              <HStack key={extId} justify="space-between" gap={2}>
                <Text fontSize="sm" color="fg.default">
                  {getExtensionDisplay(extId)}
                </Text>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => handleRemoveExtension(extId)}
                  colorScheme="red"
                >
                  {text(t, 'switchboard.nodes.common.remove', 'Remove')}
                </Button>
              </HStack>
            ))}
          </VStack>
        )}

        {/* Timeout input */}
        <NodeInput
          label={text(t, 'switchboard.nodes.transfer.timeout', 'Timeout')}
          type="number"
          value={timeout || ''}
          onChange={(e) => handleTimeoutChange(e.target.value)}
          onBlur={handleTimeoutBlur}
          placeholder="20"
          min={5}
          max={600}
        />
      </VStack>
    </BaseNode>
  )
}

export const TransferAndRolloverNode = React.memo(
  TransferAndRolloverNodeComponent,
  nodePropsAreEqual,
)
